import React from "react"
import ThemeProvider from "@material-ui/styles/ThemeProvider"

import Layout from "../../../components/layout"
import SEO from "../../../components/seo"
import PracticeRoutinePost from "../../../components/practice-routine-post"
import theme from "../../../theme"

const summary = [
  {
    level: "Preliminary to Grade 2",
    time: "15 to 25 minutes",
    focus: "Hand shape, steady pulse, five-finger patterns, simple scales, reading habits",
    weeklyHabit: "Play hands separately first, then combine slowly with a clear rhythm goal",
  },
  {
    level: "Grades 3 to 4",
    time: "30 to 40 minutes",
    focus: "Scales, voicing, pedalling basics, balance between melody and accompaniment",
    weeklyHabit: "Rotate technical work and include short sight-reading sessions through the week",
  },
  {
    level: "Grades 5 to 6",
    time: "45 to 60 minutes",
    focus: "Fluency, articulation, phrase shape, memory, controlled tempo building",
    weeklyHabit: "Record sections, fix specific bars, and separate musical work from run-throughs",
  },
  {
    level: "Grades 7 to 8",
    time: "60 to 90 minutes",
    focus: "Tone control, stylistic contrast, stamina, exam program flow",
    weeklyHabit: "Schedule mock performances, slow practice, aural revision, and recovery time",
  },
]

const sections = [
  {
    title: "Preliminary to Grade 2: make reading feel friendly",
    paragraphs: [
      "Young pianists need a routine that builds security at the keyboard. Begin with posture, relaxed hand shape, finger numbers, rhythm clapping, and simple five-finger patterns before moving into pieces.",
      "Pieces should be practised in small hands-separate sections first. Keep the tempo slow enough that the student can read, listen, and stay relaxed at the same time.",
    ],
  },
  {
    title: "Grades 3 to 4: build control between the hands",
    paragraphs: [
      "Practice can grow to 30 to 40 minutes with a clearer split between technique, pieces, sight reading, and aural work. Scales should be even and unhurried, with attention to fingering and tone.",
      "At this level, students often need help balancing melody and accompaniment. Ask which hand has the tune, then practise shaping that line while keeping the other hand lighter.",
    ],
  },
  {
    title: "Grades 5 to 6: practise detail before speed",
    paragraphs: [
      "Middle-grade piano practice works best when difficult bars are isolated. Use slow practice, dotted rhythms, small tempo increases, and careful pedalling checks rather than relying on full run-throughs.",
      "Record one section each week and listen for pulse, voicing, tone, and phrase endings. This builds independent listening, which is one of the biggest jumps at these grades.",
    ],
  },
  {
    title: "Grades 7 to 8: prepare an exam program",
    paragraphs: [
      "Advanced pianists need stamina and contrast. Plan practice across the week so each style gets attention: Baroque clarity, Classical balance, Romantic tone, contemporary rhythm, or whatever the program requires.",
      "Include mock performances, page-turn planning if needed, aural revision, and sight reading. A polished exam program depends on reliability under pressure, not only beautiful work at home.",
    ],
  },
  {
    title: "A simple weekly rhythm",
    paragraphs: [
      "Use a shared calendar to mark technique days, piece-detail days, sight-reading days, recording days, and rest. This helps students avoid repeating the same comfortable piece every day.",
      "The aim is steady progress: a little technique, a little reading, and one clear musical goal each session.",
    ],
  },
]

const PianoPracticeRoutinesAmebLevels = () => (
  <ThemeProvider theme={theme}>
    <Layout>
      <SEO
        title="Piano Practice Schedule by AMEB Level"
        description="Plan piano practice for AMEB Preliminary through Grade 8 with routines for technique, pieces, sight reading, aural work, and performance preparation."
      />
      <PracticeRoutinePost
        instrument="Piano"
        title="Piano practice routines for different AMEB levels"
        intro="A good piano routine balances reading, coordination, technique, tone, and musical shape so each practice session has a clear purpose."
        sharedCalendarUrl="https://app.schoolcalendar.me/calendar/angry-bear-67"
        image="blog-piano.png"
        imageAlt="Piano practice planning"
        summary={summary}
        sections={sections}
      />
    </Layout>
  </ThemeProvider>
)

export default PianoPracticeRoutinesAmebLevels
